import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import type { CinematicPalette } from './themePalettes';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';

const EASE = [0.22, 0.61, 0.36, 1] as const;

const copy = {
  en: {
    line1: 'Someone took time to say this to you…',
    line2a: 'Not after loss.',
    line2b: 'Not too late.',
    line2c: 'Right now.',
    line4: 'Open your heart.',
    line3: 'A Living Announcement',
  },
  es: {
    line1: 'Alguien se tomó el tiempo de decirte esto…',
    line2a: 'No después de una pérdida.',
    line2b: 'No demasiado tarde.',
    line2c: 'Ahora mismo.',
    line4: 'Abre tu corazón.',
    line3: 'Un Anuncio Viviente',
  },
  pt: {
    line1: 'Alguém reservou um tempo para te dizer isto…',
    line2a: 'Não após uma perda.',
    line2b: 'Não tarde demais.',
    line2c: 'Agora mesmo.',
    line4: 'Abra seu coração.',
    line3: 'Um Anúncio Vivo',
  },
  fr: {
    line1: "Quelqu'un a pris le temps de te dire ceci…",
    line2a: 'Pas après une perte.',
    line2b: 'Pas trop tard.',
    line2c: 'Maintenant.',
    line4: 'Ouvre ton cœur.',
    line3: 'Une Annonce Vivante',
  },
  ar: {
    line1: '…شخص ما خصص وقتًا ليقول لك هذا',
    line2a: '.ليس بعد الفقد',
    line2b: '.ليس بعد فوات الأوان',
    line2c: '.الآن',
    line4: '.افتح قلبك',
    line3: 'إعلان حيّ',
  },
  ja: {
    line1: '誰かがあなたに伝える時間を取りました…',
    line2a: '失ってからではなく。',
    line2b: '遅すぎることなく。',
    line2c: '今、伝える。',
    line4: '心を開いて。',
    line3: 'リビング・アナウンスメント',
  },
  ko: {
    line1: '누군가 당신에게 이 말을 전하기 위해 시간을 냈습니다…',
    line2a: '잃은 후가 아니라.',
    line2b: '너무 늦지 않게.',
    line2c: '바로 지금.',
    line4: '마음을 열어주세요.',
    line3: '리빙 어나운스먼트',
  },
  id: {
    line1: 'Seseorang meluangkan waktu untuk mengatakan ini padamu…',
    line2a: 'Bukan setelah kehilangan.',
    line2b: 'Bukan terlambat.',
    line2c: 'Sekarang juga.',
    line4: 'Bukalah hatimu.',
    line3: 'Pengumuman yang Hidup',
  },
  hi: {
    line1: 'किसी ने यह कहने के लिए समय निकाला…',
    line2a: 'खो देने के बाद नहीं।',
    line2b: 'बहुत देर से नहीं।',
    line2c: 'अभी।',
    line4: 'अपना दिल खोलें।',
    line3: 'एक जीवित संदेश',
  },
};

interface BuildUpSequenceProps {
  palette: CinematicPalette;
  onComplete: () => void;
  /** Custom intro beats (4 lines) — overrides the default localized phrases. */
  beats?: [string, string, string, string];
  /** Held-breath pause before first beat, in ms. */
  heldBreathMs?: number;
  /** Per-beat duration in ms. */
  beatDurationMs?: number;
}

/**
 * Four cinematic beats before the message begins.
 * Each phrase fades in, holds, and fades out at a deliberate pace.
 * Custom `beats` (chosen from the introWordBank) replace the defaults so
 * two reveals never open with identical words.
 */
export function BuildUpSequence({
  palette,
  onComplete,
  beats: customBeats,
  heldBreathMs = 0,
  beatDurationMs = 3500,
}: BuildUpSequenceProps) {
  const { language } = useLanguage();
  const t = lc(copy, language);
  const [beat, setBeat] = useState(-1);

  const phrases: [string, string, string, string] = customBeats ?? [t.line1, t.line2a, t.line2c, t.line4];

  useEffect(() => {
    // Held breath before first beat
    const startTimer = setTimeout(() => setBeat(0), heldBreathMs);
    const timers: ReturnType<typeof setTimeout>[] = [
      startTimer,
      setTimeout(() => setBeat(1), heldBreathMs + beatDurationMs),
      setTimeout(() => setBeat(2), heldBreathMs + beatDurationMs * 2),
      setTimeout(() => setBeat(3), heldBreathMs + beatDurationMs * 3),
      setTimeout(onComplete, heldBreathMs + beatDurationMs * 4),
    ];
    return () => { timers.forEach(clearTimeout); };
  }, [onComplete, heldBreathMs, beatDurationMs]);

  const textStyle = {
    color: palette.textColor,
    textShadow: `0 0 24px ${palette.accent}`,
  };

  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none px-6">
      <AnimatePresence mode="wait">
        {beat >= 0 && (
          <motion.p
            key={beat}
            initial={{ opacity: 0, y: 14, filter: 'blur(4px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            exit={{ opacity: 0, y: -10, filter: 'blur(3px)' }}
            transition={{ duration: 1.0, ease: EASE }}
            className="font-serif text-center text-2xl md:text-4xl tracking-wide max-w-[24ch] leading-snug"
            style={textStyle}
          >
            {phrases[beat]}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}
